<template>
  <Card
    v-if="hasSystems"
    :class="$style.card"
    data-test-id="DeviceNotification__widgetCard"
  >
    <Count
      :count="total"
      countSize="medium"
      :hasDivider="false"
      label="Device Notifications"
    />
    <template v-slot:body>
      <ParagraphBody>
        <WidgetList
          :list="metrics"
          @handleClickAction="navigateToDevicesInactive"
        />
      </ParagraphBody>
    </template>
  </Card>
  <NoDataWidgetCard
    v-else
    :widgetInfo="$options.noDeviceWidgetData"
    :widgetUrl="$options.deviceRoutes"
  />
</template>
<script>
import {
  Card,
  Count,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import {
  DeviceConsts, ItemTypeConsts, ItemTypeRoutes, StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import HomeWidgetConsts from 'client/Home/constants/HomeWidgetConsts';
import NoDataWidgetCard from 'client/HomePageV2/widgets/NoDataWidgetCard';
import SegmentEventMixin from 'client/HomePageV2/mixins/SegmentEventMixin';
import SystemApi from 'client/api/v2/SystemApi';
import SystemFilters from 'components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';
import Util from 'client/util/Util';
import WidgetList from 'client/HomePageV2/widgets/WidgetList';
import pluralize from 'pluralize';

const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];
const { widgetTypes } = HomeWidgetConsts;
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemModule = StoreModuleMap[ItemTypeConsts.system];
const deviceRoutes = ItemTypeRoutes[ItemTypeConsts.deviceList];
const noDeviceWidgetData = DeviceConsts.NoDeviceWidgetText;

export default {
  name: 'DeviceNotificationsWidget',
  components: {
    Card,
    Count,
    NoDataWidgetCard,
    ParagraphBody,
    WidgetList,
  },
  deviceRoutes,
  noDeviceWidgetData,
  viewAllRouteInActive: ItemTypeRoutes[ItemTypeConsts.viewDeviceInvestigationInactiveMetric],
  viewAllRouteUptimeMetric: ItemTypeRoutes[ItemTypeConsts.viewDeviceInvestigationUptimeMetric],

  mixins: [SegmentEventMixin],

  data: () => ({
    numDevicesInactive: 0,
    numDevicesUptimeHigh: 0,
    widgetType: widgetTypes.deviceNotifications,
    total: 0,
    hasSystems: false,
  }),

  computed: {
    ...mapGetters(settingsModule, ['hasSystemInsights']),

    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),

    displayPreferences() {
      return this.displaySettings.display.deviceNotifications;
    },

    metrics() {
      const metrics = [];
      if (this.displayPreferences.inactiveMetric.visible) {
        metrics.push({
          name: 'Devices inactive greater than 7 days',
          value: this.numDevicesInactive,
          url: this.$options.viewAllRouteInActive,
          fetchMethod: this.fetchDevicesInactive,
        });
      }
      // These metrics are commented out  until RR-1 has been implemented
      // @see: https://jumpcloud.atlassian.net/browse/RR-1
      if (this.hasSystemInsights) {
        if (this.displayPreferences.uptimeMetric.visible) {
          metrics.push({
            name: `Devices with uptime greater than ${pluralize('day', this.noOfConfigurableDays, true)}`,
            value: this.numDevicesUptimeHigh,
            url: this.$options.viewAllRouteUptimeMetric,
            fetchMethod: this.fetchDevicesUptimeHigh,
          });
        }
      }
      return metrics;
    },

    noOfConfigurableDays() {
      return this.displayPreferences.uptimeMetric.additionalDisplaySettings.numberOfDaysToShow;
    },
  },

  created() {
    this.getHasSystems();
    this.metrics.forEach((metric) => {
      metric.fetchMethod();
    });
  },

  methods: {
    ...mapActions(systemModule, {
      fetchSystemsTotalCount: 'fetchNoFilterTotalCount',
    }),

    fetchDevicesInactive() {
      // Get number of devices inactive for over 7 days
      const inactiveFilter = [
        SystemFilters.status(false),
        SystemFilters.inactiveDays(7),
      ];
      return SystemApi.fetch(0, 1, null, null, null, inactiveFilter)
        .then(({ totalCount }) => {
          if (typeof totalCount === 'number') {
            this.numDevicesInactive = totalCount;
            this.total += this.numDevicesInactive;
          }
        });
    },
    // These metrics are commented out  until RR-1 has been implemented
    // @see: https://jumpcloud.atlassian.net/browse/RR-1
    fetchDevicesUptimeHigh() {
      // Get number of devices with uptime greater than 30 days
      const xNoOfDays = this.noOfConfigurableDays * 24 * 60 * 60;
      return SystemInsightsApi.getAllSystemInsights('uptime', {
        bulk: true,
        params: { filter: `total_seconds:gte:${xNoOfDays}` },
      })
        .then((resp) => {
          if (resp) {
            this.numDevicesUptimeHigh = resp.length;
            this.total += this.numDevicesUptimeHigh;
          }
          return resp;
        })
        .catch(() => {
          // NOOP
        });
    },

    getHasSystems() {
      return this.fetchSystemsTotalCount()
        .then(totalCount => {
          this.hasSystems = !!totalCount;
        })
        .catch(() => {
          this.hasSystems = false;
        });
    },
    navigateToDevicesInactive(item) {
      this.triggerLinkClickedHandler(
        `Home widget small for ${item.name}`,
        'Home widget small',
        item.url,
        item.name,
      );
      Util.redirect(item.url);
    },

  },
};
</script>
<style module scoped>
@value (cardShadow1) from '~css/shadows.css';

.card {
  box-shadow: cardShadow1;
}
</style>
